import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Lock, Mail, KeyRound, User as UserIcon, Phone, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Auth() {
  const { login, register } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: form.email });
        setForgotSent(true);
      } else if (mode === "login") {
        await login(form.email, form.password);
        toast.success(t("auth.welcome_toast"));
        navigate("/");
      } else {
        await register(form);
        toast.success(t("auth.welcome_toast"));
        navigate("/");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (m) => { setMode(m); setError(""); setForgotSent(false); };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="relative min-h-screen flex items-center justify-center px-4 py-12 overflow-hidden">
      <div className="absolute top-0 left-1/3 w-[500px] h-[500px] glow-rose blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] glow-rose blur-3xl opacity-60" />
      <div className="relative w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <span className="font-serif text-3xl font-semibold text-zinc-100">FETSHOP</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-rose-400 mt-1.5">.nl</span>
        </Link>
        <div className="rounded-2xl border border-zinc-800 bg-zinc-950/80 backdrop-blur p-8 animate-fade-up noise-overlay">
          <div className="flex items-center gap-2 mb-1">
            <Lock className="h-4 w-4 text-rose-400" />
            <span className="font-mono text-[11px] uppercase tracking-widest text-rose-400">{t("auth.secure")}</span>
          </div>
          <h1 className="font-serif text-3xl font-semibold text-zinc-100 mb-6">
            {mode === "login" ? t("auth.welcome_back") : mode === "forgot" ? t("auth.forgot_title") : t("auth.create")}
          </h1>

          {error && (
            <div data-testid="auth-error" className="mb-4 rounded-lg border border-rose-900/50 bg-rose-950/30 px-4 py-2.5 text-sm text-rose-300">
              {error}
            </div>
          )}

          {mode === "forgot" && forgotSent ? (
            <div data-testid="forgot-sent" className="space-y-6">
              <div className="flex items-start gap-3 rounded-lg border border-emerald-800/50 bg-emerald-950/20 px-4 py-3 text-sm text-emerald-300">
                <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5" />
                <span>{t("auth.forgot_sent")}</span>
              </div>
              <button
                data-testid="forgot-back-button"
                onClick={() => switchMode("login")}
                className="w-full rounded-lg border border-zinc-800 bg-zinc-900/60 text-zinc-200 font-medium px-5 py-3 hover:bg-zinc-900 transition-colors"
              >
                {t("auth.back_to_login")}
              </button>
            </div>
          ) : (
          <form onSubmit={submit} className="space-y-4">
            {mode === "forgot" && (
              <p className="text-sm text-zinc-400 -mt-2">{t("auth.forgot_intro")}</p>
            )}
            {mode === "register" && (
              <>
                <Field icon={UserIcon} placeholder={t("auth.name")} value={form.name} onChange={upd("name")} testid="register-name-input" required />
                <Field icon={Phone} placeholder={t("auth.phone")} value={form.phone} onChange={upd("phone")} testid="register-phone-input" />
              </>
            )}
            <Field icon={Mail} type="email" placeholder={t("auth.email")} value={form.email} onChange={upd("email")} testid="auth-email-input" required />
            {mode !== "forgot" && (
              <Field icon={KeyRound} type="password" placeholder={t("auth.password")} value={form.password} onChange={upd("password")} testid="auth-password-input" required />
            )}

            {mode === "login" && (
              <div className="text-right -mt-1">
                <button
                  type="button"
                  data-testid="forgot-password-link"
                  onClick={() => switchMode("forgot")}
                  className="text-xs text-zinc-400 hover:text-rose-300"
                >
                  {t("auth.forgot_link")}
                </button>
              </div>
            )}

            <button
              data-testid="auth-submit-button"
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-medium px-5 py-3 shadow-lg shadow-rose-950/50 transition-all hover:scale-[1.01] active:scale-[0.98] disabled:opacity-60"
            >
              {loading ? t("auth.wait") : mode === "login" ? t("auth.login") : mode === "forgot" ? t("auth.forgot_submit") : t("auth.register")}
              {!loading && <ArrowRight className="h-4 w-4" />}
            </button>
          </form>
          )}

          <div className="mt-6 text-center text-sm text-zinc-500">
            {mode === "forgot" ? (
              <button
                data-testid="forgot-back-link"
                onClick={() => switchMode("login")}
                className="text-rose-400 hover:text-rose-300 font-medium"
              >
                {t("auth.back_to_login")}
              </button>
            ) : (
              <>
                {mode === "login" ? t("auth.no_account") : t("auth.has_account")}{" "}
                <button
                  data-testid="auth-toggle-mode"
                  onClick={() => switchMode(mode === "login" ? "register" : "login")}
                  className="text-rose-400 hover:text-rose-300 font-medium"
                >
                  {mode === "login" ? t("auth.register") : t("auth.login")}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, testid, ...props }) {
  return (
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
      <input
        data-testid={testid}
        {...props}
        className="w-full rounded-lg border border-zinc-800 bg-zinc-900/60 pl-10 pr-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-rose-700 focus:outline-none focus:ring-1 focus:ring-rose-700 transition-colors"
      />
    </div>
  );
}
